/**
 * The route table: one entry per screen. The sidebar, the route guard, the page title
 * and the contextual Help link are all derived from it.
 *
 * To add a screen (D3–D5): write the page in src/app/pages/<module>/, then point its
 * entry's `page` at it (lazy import) instead of `placeholder(...)`. Keep `perm` in step
 * with the API's permission names (lib/permissions.ts). See docs/sprints/D2.md.
 */
import {
  Activity,
  // Q9 NCR / CAPA icons
  ClipboardX,
  // Q10 recurring controls icons
  ClipboardList,
  // Q11 AI icons
  Sparkles,
  Bot,
  Tags,
  TrendingUp,
  EyeOff,
  Archive,
  BadgeCheck,
  BookCheck,
  BookLock,
  BookMarked,
  BookOpen,
  BookOpenCheck,
  Copy,
  FileBadge,
  CalendarClock,
  GitPullRequestArrow,
  FilePlus2,
  Hash,
  Building2,
  ChartNoAxesColumn,
  CircleHelp,
  Clock3,
  FileBarChart,
  FileStack,
  FileText,
  Gauge,
  History,
  House,
  Inbox,
  FilePen,
  PenLine,
  Send,
  Signature,
  UserCheck,
  Workflow,
  Settings2,
  KeyRound,
  LifeBuoy,
  Link2,
  ListChecks,
  Library,
  Recycle,
  Scale,
  Search,
  Settings,
  ShieldCheck,
  Grid3x3,
  ListTodo,
  ShieldHalf,
  FileX2,
  SlidersHorizontal,
  Timer,
  Trash2,
  Upload,
  Users,
  UsersRound,
  Wand2,
  TriangleAlert,
  FileArchive,
  UserCog,
  Boxes,
  MapPin,
  ClipboardCheck,
  ArrowRightLeft,
  ScanLine,
  Package,
  // NotaVera N1
  Stamp,
  Landmark,
  // NotaVera N8 icons
  Banknote,
  ShieldAlert,
  ShieldOff,
  FileWarning,
  UserRoundSearch,
  LockKeyhole,
  ScanEye,
  Gavel,
  ScrollText,
  Database,
  Siren,
  FileBarChart2,
  UserPlus,
  type LucideIcon,
  CalendarCheck,
  CalendarPlus,
  // NotaVera N9 icons
  GraduationCap,
  Award,
  Wrench,
  DoorOpen,
} from 'lucide-react';
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { P } from '../lib/permissions';
import { moduleOn, type ModuleKey } from '../config/features';

export type GroupId = 'notary' | 'oversight' | 'privacy' | 'documents' | 'esign' | 'controlled' | 'exceptions' | 'compliance' | 'records' | 'admin' | 'platform';

export interface NavGroup {
  id: GroupId;
  label: string;
  icon: LucideIcon;
}

export const GROUPS: NavGroup[] = [
  { id: 'notary', label: 'Notary', icon: Stamp },
  // NotaVera N7 (PRD §11): the ENA / SC officers' read-only portal + orders
  { id: 'oversight', label: 'Oversight', icon: Landmark },
  // NotaVera N8 (PRD §14.1): the Data Protection Officer's office
  { id: 'privacy', label: 'Privacy', icon: ShieldAlert },
  { id: 'documents', label: 'Documents', icon: FileStack },
  { id: 'esign', label: 'e-Sign', icon: Signature },
  { id: 'controlled', label: 'Controlled', icon: BookCheck },
  { id: 'exceptions', label: 'Exceptions', icon: TriangleAlert },
  { id: 'compliance', label: 'Compliance', icon: ShieldCheck },
  { id: 'records', label: 'Records', icon: Archive },
  { id: 'admin', label: 'Admin', icon: Settings },
  { id: 'platform', label: 'Platform', icon: Building2 },
];

/** Sidebar order: top-level links and groups. 'cabinets' is the special tree entry. */
export const NAV_ORDER: (GroupId | string)[] = ['/', 'notary', 'oversight', 'privacy', 'documents', '/cabinets', '/upload', 'controlled', 'exceptions', 'compliance', 'esign', 'records', '/approvals', '/reports', 'admin', 'platform'];

export interface RouteDef {
  path: string;
  title: string;
  icon: LucideIcon;
  /** Sidebar placement: a group id, 'top' for a top-level link, or null (not in the menu). */
  nav: GroupId | 'top' | null;
  /** Any of these permissions opens the screen (empty = any signed-in user). */
  perm: string[];
  /** Help slug for the page header link. */
  help?: string;
  /** Match only this exact path for the active menu item (lists with sub-pages below them). */
  end?: boolean;
  /** Count badge in the sidebar (e-Sign: to act / returned). */
  badge?: 'esign-action' | 'esign-returned';
  /** Nested sub-menu inside the group (e.g. Records › Physical); entries with the same `sub` sit under one collapsible header. */
  sub?: string;
  /** Q11: shown in the menu only while this AI capability is switched on (GET /ai/status); demoInbox: while GET /app-info says so. */
  feature?: 'aiGapCheck' | 'demoInbox';
  /** NotaVera N1: the switchable module this screen belongs to (config/features.ts); hidden while off. Default: from the path. */
  module?: ModuleKey;
  page: ComponentType | LazyExoticComponent<ComponentType>;
}

const Home = lazy(() => import('./pages/home/HomePage'));
const DocumentsPage = lazy(() => import('./pages/documents/DocumentsPage'));
const CabinetsPage = lazy(() => import('./pages/cabinets/CabinetsPage'));
const UploadPage = lazy(() => import('./pages/upload/UploadPage'));
const InboxPage = lazy(() => import('./pages/inbox/InboxPage'));
const ProcessingPage = lazy(() => import('./pages/processing/ProcessingPage'));
const RulesPage = lazy(() => import('./pages/admin/RulesPage'));
const SearchPage = lazy(() => import('./pages/search/SearchPage'));
// D4
const ViewerPage = lazy(() => import('./pages/viewer/ViewerPage'));
const TrashPage = lazy(() => import('./pages/trash/TrashPage'));
const SharesPage = lazy(() => import('./pages/shares/SharesPage'));
const HelpIndex = lazy(() => import('./help/HelpIndex'));
// Q11 AI
const AiGapCheckPage = lazy(() => import('./pages/ai/GapCheckPage'));
const AiGapRunPage = lazy(() => import('./pages/ai/GapRunPage'));
const PlatformAiPage = lazy(() => import('./pages/ai/PlatformAiPage'));
// D5
const RetentionPage = lazy(() => import('./pages/records/RetentionPage'));
const HoldsPage = lazy(() => import('./pages/records/HoldsPage'));
const DispositionPage = lazy(() => import('./pages/records/DispositionPage'));
const ApprovalsPage = lazy(() => import('./pages/records/ApprovalsPage'));
const NotificationsPage = lazy(() => import('./pages/records/NotificationsPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const GroupsPage = lazy(() => import('./pages/admin/GroupsPage'));
const RolesPage = lazy(() => import('./pages/admin/RolesPage'));
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'));
const ActivityPage = lazy(() => import('./pages/admin/ActivityPage'));
const IntegrityPage = lazy(() => import('./pages/admin/IntegrityPage'));
const ApiKeysPage = lazy(() => import('./pages/admin/ApiKeysPage'));
const ImportPage = lazy(() => import('./pages/admin/ImportPage'));
const SupportAccessPage = lazy(() => import('./pages/admin/SupportAccessPage'));
const OrgsPage = lazy(() => import('./pages/platform/OrganisationsPage'));
const SupportSessionsPage = lazy(() => import('./pages/platform/SupportSessionsPage'));
const QueuePage = lazy(() => import('./pages/platform/QueuePage'));
// e-Sign (ES3)
const EsignInbox = lazy(() => import('./pages/esign/InboxPage'));
const EsignNew = lazy(() => import('./pages/esign/NewRequestPage'));
const EsignRequest = lazy(() => import('./pages/esign/RequestPage'));
const EsignMatrices = lazy(() => import('./pages/esign/MatricesPage'));
const EsignDelegations = lazy(() => import('./pages/esign/DelegationsPage'));
const EsignSettings = lazy(() => import('./pages/esign/EsignSettingsPage'));
// e-Sign (ES4)
const EsignReports = lazy(() => import('./pages/esign/ReportsPage'));
// QXM Q1: controlled documents
const ControlledList = lazy(() => import('./pages/controlled/ControlledListPage'));
const ControlledNew = lazy(() => import('./pages/controlled/NewControlledWizard'));
const ControlledDetail = lazy(() => import('./pages/controlled/ControlledDetailPage'));
const NumberingPage = lazy(() => import('./pages/controlled/NumberingPage'));
// QXM Q2: change requests, periodic review
const ChangesList = lazy(() => import('./pages/controlled/ChangesListPage'));
const ChangeRequest = lazy(() => import('./pages/controlled/ChangeRequestPage'));
const ReviewDue = lazy(() => import('./pages/controlled/ReviewDuePage'));
// QXM Q3: acknowledgements, copies, external documents, certificates
const AcksMine = lazy(() => import('./pages/controlled/AcksMinePage'));
const AcksRegister = lazy(() => import('./pages/controlled/AcksPage'));
const CopiesPage = lazy(() => import('./pages/controlled/CopiesPage'));
const ExternalPage = lazy(() => import('./pages/controlled/ExternalPage'));
const CertificatesPage = lazy(() => import('./pages/records/CertificatesPage'));
// QXM Q4: Compliance (clause library)
const ClausesPage = lazy(() => import('./pages/compliance/ClausesPage'));
const ReadinessPage = lazy(() => import('./pages/compliance/ReadinessPage'));
const GapListPage = lazy(() => import('./pages/compliance/GapListPage'));
// QXM Q5
const EvidencePacksPage = lazy(() => import('./pages/compliance/EvidencePacksPage'));
const RedactionEditor = lazy(() => import('./pages/redaction/RedactionEditorPage'));
const RedactionsPage = lazy(() => import('./pages/redaction/RedactionsPage'));
const AccessReviewsPage = lazy(() => import('./pages/admin/AccessReviewsPage'));
const CabinetsAdminPage = lazy(() => import('./pages/admin/CabinetsAdminPage'));
// ── QXM Q9: NCR / CAPA (kept in its own block for the Q8 merge) ──
const NcrListPage = lazy(() => import('./pages/capa/NcrListPage'));
const NcrPage = lazy(() => import('./pages/capa/NcrPage'));
const NcrTrendsPage = lazy(() => import('./pages/capa/NcrTrendsPage'));
const NcrCategoriesPage = lazy(() => import('./pages/capa/NcrCategoriesPage'));
// ── end Q9 block ──
// ── QXM Q10: recurring controls (own block) ──
const ControlsPage = lazy(() => import('./pages/controls/ControlsPage'));
const MyControlsPage = lazy(() => import('./pages/controls/MyControlsPage'));
const VerifyQueuePage = lazy(() => import('./pages/controls/VerifyQueuePage'));
const ControlPerformancePage = lazy(() => import('./pages/controls/ControlPerformancePage'));
const ControlInstancePage = lazy(() => import('./pages/controls/ControlInstancePage'));
// ── end Q10 block ──
// QXM Q4: Exception Center
const ExOverview = lazy(() => import('./pages/exceptions/OverviewPage'));
const ExOpen = lazy(() => import('./pages/exceptions/OpenItemsPage'));
const ExRules = lazy(() => import('./pages/exceptions/RulesPage'));
const ExRuns = lazy(() => import('./pages/exceptions/RunsPage'));
// QXM Q8: Records › Physical (RFID copies, boxes, files, locations)
const PhysicalItems = lazy(() => import('./pages/physical/ItemsPage'));
const PhysicalItem = lazy(() => import('./pages/physical/ItemPage'));
const PhysicalLocations = lazy(() => import('./pages/physical/LocationsPage'));
const PhysicalCounts = lazy(() => import('./pages/physical/CountsPage'));
const PhysicalMoves = lazy(() => import('./pages/physical/MovesPage'));
const PhysicalBatches = lazy(() => import('./pages/physical/BatchesPage'));
// NotaVera N1: ENP registry + commissions
const EnpRegistry = lazy(() => import('./pages/enp/RegistryPage'));
const EnpDetail = lazy(() => import('./pages/enp/EnpDetailPage'));
const EnpMine = lazy(() => import('./pages/enp/MyCommissionPage'));
const EnpOnboard = lazy(() => import('./pages/enp/OnboardPage'));
// NotaVera N5 (PRD §8): the ENP's seal
const SealPage = lazy(() => import('./pages/seal/SealPage'));
// NotaVera N6 (PRD §9): the Electronic Notarial Book
const BookPage = lazy(() => import('./pages/book/BookPage'));
// NotaVera N7 (PRD §11): SC monthly report, ENA orders inbox, the oversight portal
const ScReportsPage = lazy(() => import('./pages/sc/ScReportsPage'));
const OrdersPage = lazy(() => import('./pages/ena/OrdersPage'));
const OversightPage = lazy(() => import('./pages/ena/OversightPage'));
// NotaVera N8 (PRD §12, §14.1, §14.2): payments, privacy office, break-glass, confidentiality, fraud signals
const PaymentsPage = lazy(() => import('./pages/pay/PaymentsPage'));
const PrivacyPage = lazy(() => import('./pages/privacy/PrivacyPage'));
const BreakGlassPage = lazy(() => import('./pages/privacy/BreakGlassPage'));
const ConfidentialityPage = lazy(() => import('./pages/privacy/ConfidentialityPage'));
const FraudPage = lazy(() => import('./pages/fraud/FraudPage'));
const privacy = (section: 'overview' | 'breaches' | 'requests' | 'break-glass' | 'staff') => () => <PrivacyPage section={section} />;
const oversight = (section: 'overview' | 'books' | 'documents' | 'incidents' | 'reports' | 'cndb') => () => <OversightPage section={section} />;
// NotaVera N2 (PRD §5): sessions + party e-KYC review
const SessionsPage = lazy(() => import('./pages/sessions/SessionsPage'));
const NewSessionPage = lazy(() => import('./pages/sessions/NewSessionPage'));
const SessionPage = lazy(() => import('./pages/sessions/SessionPage'));
const DemoInboxPage = lazy(() => import('./pages/sessions/DemoInboxPage'));
// NotaVera N4 (PRD §6.2): the REN video room
const RoomPage = lazy(() => import('./pages/sessions/RoomPage'));
// NotaVera N9 (PRD §13, §14.3, §14.4): training, maintenance notices, provider exit
const TrainingPage = lazy(() => import('./pages/training/TrainingPage'));
const TrainingCertificatesPage = lazy(() => import('./pages/training/TrainingPage').then((m) => ({ default: m.CertificatesPage })));
const LessonPage = lazy(() => import('./pages/training/LessonPage'));
const MaintenancePage = lazy(() => import('./pages/ops/MaintenancePage'));
const ExitPage = lazy(() => import('./pages/exit/ExitPage'));
const ProviderExitPage = lazy(() => import('./pages/exit/ProviderExitPage'));

/** Read rights on Records › Physical (API: physical.scan | physical.manage | audit.view). */
const PHYS_READ = [P.physicalScan, P.physicalManage, P.audit];


const ALL_ROUTES: RouteDef[] = [
  { path: '/', title: 'Home', icon: House, nav: 'top', perm: [], help: 'getting-started', page: Home },

  // ── NotaVera N1 (PRD §4): Notary › My commission, ENP registry, Onboard ENP ──
  { path: '/enp/me', title: 'My commission', icon: BadgeCheck, nav: 'notary', perm: [P.enpSelf], help: 'commissions', page: EnpMine },
  { path: '/enp/seal', title: 'My seal', icon: Stamp, nav: 'notary', perm: [P.enpSelf], help: 'my-seal', page: SealPage },
  { path: '/enp/book', title: 'Book', icon: BookOpen, nav: 'notary', perm: [P.enpSelf], help: 'notarial-book', page: BookPage },
  // ── NotaVera N7 (PRD §11): Notary › SC reports, ENA orders (ENP side) ──
  { path: '/enp/sc-reports', title: 'SC reports', icon: FileBarChart2, nav: 'notary', perm: [P.enpSelf], help: 'sc-monthly-report', page: ScReportsPage },
  { path: '/enp/ena-orders', title: 'ENA orders', icon: Gavel, nav: 'notary', perm: [P.enpSelf], help: 'ena-orders', page: OrdersPage },
  // ── NotaVera N7 (PRD §11 FR-SC-03/04): Oversight (ENA / SC officers, read-only; orders) ──
  { path: '/oversight', title: 'Overview', icon: Landmark, nav: 'oversight', perm: [P.enaRead], help: 'ena-portal', end: true, page: oversight('overview') },
  { path: '/oversight/books', title: 'Books', icon: BookOpen, nav: 'oversight', perm: [P.enaRead], help: 'ena-portal', page: oversight('books') },
  { path: '/oversight/documents', title: 'Documents', icon: ScrollText, nav: 'oversight', perm: [P.enaRead], help: 'ena-portal', page: oversight('documents') },
  { path: '/oversight/incidents', title: 'Incidents', icon: Siren, nav: 'oversight', perm: [P.enaRead], help: 'ena-portal', page: oversight('incidents') },
  { path: '/oversight/reports', title: 'Monthly reports', icon: FileBarChart2, nav: 'oversight', perm: [P.enaRead], help: 'ena-portal', page: oversight('reports') },
  { path: '/oversight/orders', title: 'Orders', icon: Gavel, nav: 'oversight', perm: [P.enaOrders, P.enaRead, P.platformEnps], help: 'ena-orders', page: OrdersPage },
  { path: '/oversight/cndb', title: 'CNDB queue', icon: Database, nav: 'oversight', perm: [P.enaRead, P.platformEnps], help: 'ena-portal', page: oversight('cndb') },
  // ── NotaVera N8: Notary › Fees & payments, Fraud signals; Privacy (DPO); confidentiality; Platform › Break-glass ──
  { path: '/enp/payments', title: 'Fees & payments', icon: Banknote, nav: 'notary', perm: [P.enpSelf, P.notaryPrepare], help: 'payments', page: PaymentsPage },
  { path: '/fraud', title: 'Fraud signals', icon: ScanEye, nav: 'notary', perm: [P.enpSelf, P.notaryPrepare, P.platformEnps], help: 'fraud-signals', page: FraudPage },
  { path: '/privacy', title: 'Dashboard', icon: ShieldAlert, nav: 'privacy', perm: [P.privacyManage], help: 'privacy-office', end: true, page: privacy('overview') },
  { path: '/privacy/breaches', title: 'Breach register', icon: FileWarning, nav: 'privacy', perm: [P.privacyManage], help: 'privacy-office', page: privacy('breaches') },
  { path: '/privacy/requests', title: 'Data subject requests', icon: UserRoundSearch, nav: 'privacy', perm: [P.privacyManage], help: 'privacy-office', page: privacy('requests') },
  { path: '/privacy/break-glass', title: 'Break-glass reviews', icon: ShieldOff, nav: 'privacy', perm: [P.privacyManage], help: 'break-glass', page: privacy('break-glass') },
  { path: '/privacy/staff', title: 'Undertakings', icon: Users, nav: 'privacy', perm: [P.privacyManage], help: 'break-glass', page: privacy('staff') },
  { path: '/confidentiality', title: 'Confidentiality undertaking', icon: ShieldCheck, nav: null, perm: [], help: 'break-glass', page: ConfidentialityPage },
  { path: '/platform/break-glass', title: 'Break-glass', icon: LockKeyhole, nav: 'platform', perm: [P.platformSupport], help: 'break-glass', page: BreakGlassPage },
  { path: '/enp', title: 'ENP registry', icon: Stamp, nav: 'notary', perm: [P.enpView, P.enaRead, P.platformEnps], help: 'enp-registry', end: true, page: EnpRegistry },
  { path: '/enp/new', title: 'Onboard an ENP', icon: UserPlus, nav: 'notary', perm: [P.platformEnps], help: 'enp-registry', page: EnpOnboard },
  { path: '/enp/:id', title: 'ENP', icon: Stamp, nav: null, perm: [P.enpView, P.enaRead, P.platformEnps, P.enpSelf], help: 'commissions', page: EnpDetail },
  // ── NotaVera N2 (PRD §5): Notary › Sessions ──
  { path: '/sessions', title: 'Sessions', icon: CalendarCheck, nav: 'notary', perm: [P.notaryPrepare, P.notaryAct], help: 'inviting-parties', end: true, page: SessionsPage },
  { path: '/sessions/new', title: 'New session', icon: CalendarPlus, nav: null, perm: [P.notaryPrepare, P.notaryAct], help: 'inviting-parties', page: NewSessionPage },
  { path: '/sessions/:id', title: 'Session', icon: CalendarCheck, nav: null, perm: [P.notaryPrepare, P.notaryAct], help: 'reviewing-identity', page: SessionPage },
  { path: '/demo-inbox', title: 'Demo inbox', icon: Inbox, nav: 'notary', perm: [P.notaryPrepare, P.notaryAct, P.platformEnps], help: 'inviting-parties', feature: 'demoInbox', page: DemoInboxPage },
  { path: '/sessions/:id/room', title: 'Video room', icon: CalendarCheck, nav: null, perm: [P.notaryPrepare, P.notaryAct], help: 'video-room', page: RoomPage },
  // ── NotaVera N9: Notary › Training, Exit & export; Oversight › Training certificates; Platform › Maintenance, Provider exit ──
  { path: '/training', title: 'Training', icon: GraduationCap, nav: 'notary', perm: [P.enpSelf, P.notaryPrepare], help: 'training', end: true, page: TrainingPage },
  { path: '/training/certificates', title: 'Training certificates', icon: Award, nav: 'oversight', perm: [P.enaRead, P.platformEnps], help: 'training', page: TrainingCertificatesPage },
  { path: '/training/:code', title: 'Training module', icon: GraduationCap, nav: null, perm: [P.enpSelf, P.notaryPrepare], help: 'training', page: LessonPage },
  { path: '/enp/exit', title: 'Exit & export', icon: DoorOpen, nav: 'notary', perm: [P.enpSelf], help: 'provider-exit', page: ExitPage },
  { path: '/platform/maintenance', title: 'Maintenance notices', icon: Wrench, nav: 'platform', perm: [P.platformHealth], help: 'maintenance-status', page: MaintenancePage },
  { path: '/platform/exit', title: 'Provider exit', icon: DoorOpen, nav: 'platform', perm: [P.platformEnps], help: 'provider-exit', page: ProviderExitPage },
  { path: '/search', title: 'Search', icon: Search, nav: null, perm: [P.docsView], help: 'finding-documents', page: SearchPage },

  { path: '/documents', title: 'All documents', icon: FileStack, nav: 'documents', perm: [P.docsView], help: 'finding-documents', end: true, page: DocumentsPage },
  { path: '/documents/inbox', title: 'Inbox', icon: Inbox, nav: 'documents', perm: [P.inboxReview], help: 'inbox-rules', page: InboxPage },
  { path: '/documents/recent', title: 'Recently added', icon: Clock3, nav: 'documents', perm: [P.docsView], help: 'finding-documents', page: DocumentsPage },
  { path: '/documents/trash', title: 'Trash', icon: Trash2, nav: 'documents', perm: [P.docsDelete], help: 'versions-trash', page: TrashPage },
  {
    path: '/documents/:id',
    title: 'Document',
    icon: FileText,
    nav: null,
    perm: [P.docsView],
    help: 'viewer',
    page: ViewerPage,
  },
  // QXM Q6 (PRD §12.2 SC-04): the redaction editor of one document.
  { path: '/documents/:id/redact', title: 'Redact', icon: EyeOff, nav: null, perm: [P.docsRedact], help: 'redaction', page: RedactionEditor },

  { path: '/cabinets', title: 'Cabinets', icon: Library, nav: 'top', perm: [P.docsView], help: 'finding-documents', page: CabinetsPage },
  { path: '/cabinets/:cabinetId', title: 'Cabinets', icon: Library, nav: null, perm: [P.docsView], help: 'finding-documents', page: CabinetsPage },
  { path: '/cabinets/:cabinetId/f/:folderId', title: 'Cabinets', icon: Library, nav: null, perm: [P.docsView], help: 'finding-documents', page: CabinetsPage },

  { path: '/upload', title: 'Upload', icon: Upload, nav: 'top', perm: [P.docsUpload], help: 'uploading', page: UploadPage },

  { path: '/esign', title: 'Inbox', icon: PenLine, nav: 'esign', perm: [], help: 'esign-signing', end: true, badge: 'esign-action', page: EsignInbox },
  { path: '/esign/sent', title: 'Sent', icon: Send, nav: 'esign', perm: [P.esignSend], help: 'esign-sending', badge: 'esign-returned', page: EsignInbox },
  { path: '/esign/new', title: 'New request', icon: FilePen, nav: 'esign', perm: [P.esignSend], help: 'esign-sending', page: EsignNew },
  { path: '/esign/matrices', title: 'Templates & matrices', icon: Workflow, nav: 'esign', perm: [P.workflows], help: 'esign-matrices', page: EsignMatrices },
  { path: '/esign/delegations', title: 'Delegations', icon: UserCheck, nav: 'esign', perm: [], help: 'esign-delegation', page: EsignDelegations },
  { path: '/esign/reports', title: 'e-Sign reports', icon: ChartNoAxesColumn, nav: 'esign', perm: [P.reports], help: 'esign-reports', page: EsignReports },
  { path: '/esign/settings', title: 'e-Sign settings', icon: Settings2, nav: 'esign', perm: [P.settings, P.typesManage], help: 'esign-matrices', page: EsignSettings },
  { path: '/esign/requests/:id', title: 'Signing request', icon: PenLine, nav: null, perm: [], help: 'esign-signing', page: EsignRequest },

  // QXM controlled documents (PRD §5). Readers see the register with effective documents only.
  { path: '/controlled', title: 'Register', icon: BookCheck, nav: 'controlled', perm: [P.docsView], help: 'controlled-documents', end: true, page: ControlledList },
  { path: '/controlled/new', title: 'New controlled document', icon: FilePlus2, nav: 'controlled', perm: [P.controlledManage], help: 'controlled-documents', page: ControlledNew },
  { path: '/controlled/changes', title: 'Change requests', icon: GitPullRequestArrow, nav: 'controlled', perm: [P.docsView], help: 'change-requests', end: true, page: ChangesList },
  { path: '/controlled/changes/:id', title: 'Change request', icon: GitPullRequestArrow, nav: null, perm: [P.docsView], help: 'change-requests', page: ChangeRequest },
  { path: '/controlled/review', title: 'Due for review', icon: CalendarClock, nav: 'controlled', perm: [P.docsView], help: 'periodic-review', page: ReviewDue },
  { path: '/controlled/acks', title: 'Acknowledgements', icon: BookOpenCheck, nav: 'controlled', perm: [P.acksAssign, P.controlledManage, P.controlledReview, P.audit], help: 'acknowledgements', page: AcksRegister },
  { path: '/controlled/copies', title: 'Copies', icon: Copy, nav: 'controlled', perm: [P.docsView], help: 'controlled-copies', page: CopiesPage },
  { path: '/controlled/external', title: 'External documents', icon: BookMarked, nav: 'controlled', perm: [P.docsView], help: 'external-documents', end: true, page: ExternalPage },
  { path: '/controlled/external/:id', title: 'External document', icon: BookMarked, nav: null, perm: [P.docsView], help: 'external-documents', page: ExternalPage },
  { path: '/acks', title: 'My acknowledgements', icon: BookOpenCheck, nav: null, perm: [], help: 'acknowledgements', page: AcksMine },
  { path: '/controlled/:id', title: 'Controlled document', icon: BookCheck, nav: null, perm: [P.docsView], help: 'controlled-documents', page: ControlledDetail },

  // QXM Compliance (PRD §13): clause library, readiness, gaps
  { path: '/compliance/clauses', title: 'Clauses', icon: ShieldCheck, nav: 'compliance', perm: [P.exceptionsView, P.controlledManage, P.audit], help: 'clause-library', page: ClausesPage },
  { path: '/compliance/readiness', title: 'Readiness', icon: Grid3x3, nav: 'compliance', perm: [P.exceptionsView, P.controlledManage, P.audit], help: 'readiness', page: ReadinessPage },
  { path: '/compliance/gaps', title: 'Gap list', icon: ListTodo, nav: 'compliance', perm: [P.exceptionsView, P.controlledManage, P.audit], help: 'readiness', page: GapListPage },
  // QXM Q5 (PRD §11 EP-03): ISO audit evidence packs (Auditors, Document Controller, Records Officer, Org Admin).
  { path: '/compliance/evidence-packs', title: 'Evidence packs', icon: FileArchive, nav: 'compliance', perm: [P.evidencePack], help: 'evidence-pack', page: EvidencePacksPage },
  // ── QXM Q9 (PRD §14): Compliance › Nonconformities, NCR trends, NCR categories (own block for the Q8 merge) ──
  { path: '/compliance/ncrs', title: 'Nonconformities', icon: ClipboardX, nav: 'compliance', perm: [P.capaView, P.capaManage], help: 'ncr-capa', end: true, page: NcrListPage },
  { path: '/compliance/ncrs/:id', title: 'Nonconformity', icon: ClipboardX, nav: null, perm: [], help: 'ncr-capa', page: NcrPage },
  { path: '/compliance/ncr-trends', title: 'NCR trends', icon: TrendingUp, nav: 'compliance', perm: [P.capaView, P.capaManage], help: 'ncr-capa', page: NcrTrendsPage },
  { path: '/compliance/ncr-categories', title: 'NCR categories', icon: Tags, nav: 'compliance', perm: [P.capaManage], help: 'ncr-capa', page: NcrCategoriesPage },
  // ── end Q9 block ──
  // ── QXM Q10 (PRD §15): Compliance › Controls, My controls, To verify, Control performance (own block; static paths before :id) ──
  { path: '/compliance/controls', title: 'Controls', icon: ClipboardCheck, nav: 'compliance', sub: 'Controls', perm: [P.controlsView, P.controlsManage], help: 'recurring-controls', end: true, page: ControlsPage },
  { path: '/compliance/controls/mine', title: 'My controls', icon: ClipboardList, nav: 'compliance', sub: 'Controls', perm: [], help: 'recurring-controls', page: MyControlsPage },
  { path: '/compliance/controls/verify', title: 'To verify', icon: ListChecks, nav: 'compliance', sub: 'Controls', perm: [P.controlsVerify], help: 'recurring-controls', page: VerifyQueuePage },
  { path: '/compliance/controls/performance', title: 'Control performance', icon: Gauge, nav: 'compliance', sub: 'Controls', perm: [P.controlsView, P.controlsManage], help: 'recurring-controls', page: ControlPerformancePage },
  { path: '/compliance/controls/instances/:id', title: 'Control', icon: ClipboardCheck, nav: null, perm: [], help: 'recurring-controls', page: ControlInstancePage },
  // ── end Q10 block ──
  // ── QXM Q11 (PRD §16): Compliance › AI › Gap check — hidden unless the AI is on (own block; static paths before :id) ──
  { path: '/compliance/ai/gap-check', title: 'Gap check', icon: Sparkles, nav: 'compliance', sub: 'AI', feature: 'aiGapCheck', perm: [P.aiGapCheck], help: 'ai-gap-check', end: true, page: AiGapCheckPage },
  { path: '/compliance/ai/gap-check/:id', title: 'AI gap check', icon: Sparkles, nav: null, perm: [P.aiGapCheck], help: 'ai-gap-check', page: AiGapRunPage },
  // ── end Q11 block ──

  // QXM Exception Center (PRD §10)
  { path: '/exceptions', title: 'Overview', icon: Gauge, nav: 'exceptions', perm: [P.exceptionsView], help: 'exception-center', end: true, page: ExOverview },
  { path: '/exceptions/open', title: 'Open items', icon: TriangleAlert, nav: 'exceptions', perm: [P.exceptionsView], help: 'exception-center', page: ExOpen },
  { path: '/exceptions/rules', title: 'Rules', icon: ListChecks, nav: 'exceptions', perm: [P.exceptionsView], help: 'exception-rules', page: ExRules },
  { path: '/exceptions/runs', title: 'Runs', icon: History, nav: 'exceptions', perm: [P.exceptionsView], help: 'exception-center', page: ExRuns },
  // Q4 review M7: owners without exceptions.view open their own rows (the API decides; 404 otherwise).
  { path: '/exceptions/mine', title: 'My exceptions', icon: TriangleAlert, nav: null, perm: [], help: 'exception-center', page: ExOpen },
  { path: '/exceptions/:id', title: 'Exception', icon: TriangleAlert, nav: null, perm: [], help: 'exception-center', page: ExOpen },

  {
    path: '/records/retention',
    title: 'Retention',
    icon: Timer,
    nav: 'records',
    perm: [P.retention, P.dispositionPropose],
    end: true,
    help: 'retention',
    page: RetentionPage,
  },
  {
    path: '/records/holds',
    title: 'Legal holds',
    icon: BookLock,
    nav: 'records',
    perm: [P.holds, P.audit],
    end: true,
    help: 'retention',
    page: HoldsPage,
  },
  {
    path: '/records/disposition',
    title: 'Disposition',
    icon: FileX2,
    nav: 'records',
    perm: [P.dispositionPropose, P.dispositionApprove, P.audit],
    end: true,
    help: 'retention',
    page: DispositionPage,
  },

  { path: '/records/redactions', title: 'Redactions', icon: EyeOff, nav: 'records', perm: [P.docsRedact], help: 'redaction', page: RedactionsPage },
  { path: '/records/certificates', title: 'Certificates', icon: FileBadge, nav: 'records', perm: [P.docsView], help: 'records-lock', page: CertificatesPage },
  // QXM Q8 (PRD §18 RF-08): Records › Physical sub-menu.
  { path: '/records/physical', title: 'Items', icon: Boxes, nav: 'records', sub: 'Physical', perm: PHYS_READ, help: 'physical-rfid', end: true, page: PhysicalItems },
  { path: '/records/physical/locations', title: 'Locations', icon: MapPin, nav: 'records', sub: 'Physical', perm: PHYS_READ, help: 'physical-rfid', page: PhysicalLocations },
  { path: '/records/physical/counts', title: 'Counts', icon: ClipboardCheck, nav: 'records', sub: 'Physical', perm: PHYS_READ, help: 'physical-rfid', end: true, page: PhysicalCounts },
  { path: '/records/physical/moves', title: 'Moves', icon: ArrowRightLeft, nav: 'records', sub: 'Physical', perm: PHYS_READ, help: 'physical-rfid', page: PhysicalMoves },
  { path: '/records/physical/batches', title: 'Scan batches', icon: ScanLine, nav: 'records', sub: 'Physical', perm: [P.physicalManage, P.audit], help: 'physical-rfid', page: PhysicalBatches },
  { path: '/records/physical/counts/:id', title: 'Count', icon: ClipboardCheck, nav: null, perm: PHYS_READ, help: 'physical-rfid', page: PhysicalCounts },
  { path: '/records/physical/:id', title: 'Physical item', icon: Package, nav: null, perm: PHYS_READ, help: 'physical-rfid', page: PhysicalItem },

  {
    path: '/approvals',
    title: 'Approvals',
    icon: BadgeCheck,
    nav: 'top',
    perm: [P.approvals, P.dispositionApprove],
    help: 'retention',
    page: ApprovalsPage,
  },
  {
    path: '/reports',
    title: 'Reports',
    icon: FileBarChart,
    nav: 'top',
    perm: [P.reports],
    help: 'reports',
    page: ReportsPage,
  },

  { path: '/admin/users', title: 'Users', icon: Users, nav: 'admin', perm: [P.users], help: 'admin', page: UsersPage },
  { path: '/admin/groups', title: 'Groups', icon: UsersRound, nav: 'admin', perm: [P.groups], help: 'admin', page: GroupsPage },
  { path: '/admin/roles', title: 'Roles', icon: KeyRound, nav: 'admin', perm: [P.roles], help: 'admin', page: RolesPage },
  // QXM Q5 (PRD §12.3 SC-07): access reviews.
  { path: '/admin/access-reviews', title: 'Access reviews', icon: UserCog, nav: 'admin', perm: [P.accessReview, P.audit], help: 'access-review', end: true, page: AccessReviewsPage },
  { path: '/admin/access-reviews/:id', title: 'Access review', icon: UserCog, nav: null, perm: [P.accessReview, P.audit], help: 'access-review', page: AccessReviewsPage },
  {
    path: '/admin/cabinets',
    title: 'Cabinets and types',
    icon: SlidersHorizontal,
    nav: 'admin',
    perm: [P.cabinetsManage, P.typesManage],
    help: 'classification',
    page: CabinetsAdminPage,
  },
  { path: '/admin/rules', title: 'Matching rules', icon: Wand2, nav: 'admin', perm: [P.rulesManage], help: 'inbox-rules', page: RulesPage },
  { path: '/admin/settings', title: 'Settings', icon: Gauge, nav: 'admin', perm: [P.settings], help: 'admin', page: SettingsPage },
  { path: '/admin/activity', title: 'Activity log', icon: History, nav: 'admin', perm: [P.audit], help: 'admin', page: ActivityPage },
  { path: '/admin/shares', title: 'Share links', icon: Link2, nav: 'admin', perm: [P.settings], help: 'download-share', page: SharesPage },

  { path: '/records/holds/:id', title: 'Legal hold', icon: BookLock, nav: null, perm: [P.holds, P.audit, P.retention], help: 'retention', page: HoldsPage },
  { path: '/records/disposition/:id', title: 'Disposal batch', icon: FileX2, nav: null, perm: [P.dispositionPropose, P.dispositionApprove, P.audit], help: 'retention', page: DispositionPage },
  { path: '/admin/integrity', title: 'Integrity checks', icon: ShieldHalf, nav: 'admin', perm: [P.integrity], help: 'admin', end: true, page: IntegrityPage },
  { path: '/admin/integrity/:id', title: 'Integrity check', icon: ShieldHalf, nav: null, perm: [P.integrity], help: 'admin', page: IntegrityPage },
  { path: '/admin/import', title: 'Import', icon: Upload, nav: 'admin', perm: [P.importRun], help: 'admin', page: ImportPage },
  { path: '/admin/numbering', title: 'Numbering & controlled types', icon: Hash, nav: 'admin', perm: [P.controlledManage, P.typesManage], help: 'controlled-documents', page: NumberingPage },
  { path: '/admin/api-keys', title: 'API keys', icon: KeyRound, nav: 'admin', perm: [P.apiKeys], help: 'admin', page: ApiKeysPage },

  { path: '/platform/orgs', title: 'Organisations', icon: Building2, nav: 'platform', perm: [P.platformOrgs], help: 'admin', page: OrgsPage },
  { path: '/platform/quotas', title: 'Quotas', icon: Scale, nav: 'platform', perm: [P.platformOrgs], help: 'admin', page: OrgsPage },
  { path: '/platform/support', title: 'Support sessions', icon: LifeBuoy, nav: 'platform', perm: [P.platformSupport], help: 'admin', page: SupportSessionsPage },
  // Q11: the server-wide AI switch + write-only key (ITCSI).
  { path: '/platform/ai', title: 'AI provider', icon: Bot, nav: 'platform', perm: [P.platformOrgs], help: 'ask-qxm', page: PlatformAiPage },
  { path: '/platform/queue', title: 'Queue health', icon: Activity, nav: 'platform', perm: [P.platformHealth], help: 'admin', page: QueuePage },

  { path: '/notifications', title: 'Notifications', icon: ListChecks, nav: null, perm: [], page: NotificationsPage },
  { path: '/processing', title: 'Processing', icon: Recycle, nav: null, perm: [P.docsUpload, P.inboxReview], help: 'uploading', page: ProcessingPage },
  { path: '/support', title: 'Support access', icon: ShieldHalf, nav: null, perm: [P.supportApprove], help: 'admin', page: SupportAccessPage },

  { path: '/help', title: 'Help', icon: CircleHelp, nav: 'top', perm: [], page: HelpIndex },
];

/** NotaVera N1: which switchable module a screen belongs to, by path (pure; exported for tests). */
export function moduleOfPath(path: string): ModuleKey | undefined {
  const rules: [RegExp, ModuleKey][] = [
    [/^\/(controlled|acks)(\/|$)|^\/admin\/numbering$/, 'controlled'],
    [/^\/exceptions(\/|$)/, 'exceptions'],
    [/^\/compliance\/(clauses|readiness|gaps)$/, 'compliance'],
    [/^\/compliance\/evidence-packs$/, 'evidencePack'],
    [/^\/compliance\/ncr/, 'capa'],
    [/^\/compliance\/controls(\/|$)/, 'controls'],
    [/^\/compliance\/ai(\/|$)|^\/platform\/ai$/, 'ai'],
    [/^\/records\/physical(\/|$)/, 'physical'],
    [/^\/records\/redactions$|^\/documents\/:id\/redact$/, 'redaction'],
    // NotaVera N9 cleanup: inherited QXM screens that do not belong in an Electronic Notarization Facility (web only)
    [/^\/(documents|search|cabinets|upload|processing)(\/|$)|^\/admin\/(cabinets|rules|import|shares)$/, 'library'],
    [/^\/esign(\/|$)/, 'esign'],
    [/^\/records\/(retention|holds|disposition|certificates)(\/|$)|^\/approvals$/, 'records'],
    [/^\/reports$/, 'qxmReports'],
    [/^\/platform\/support$|^\/support$/, 'support'],
  ];
  return rules.find(([re]) => re.test(path))?.[1];
}

/** Every screen, before the feature flags (tests check the hiding). */
export const ROUTES_ALL: RouteDef[] = ALL_ROUTES.map((r) => ({ ...r, module: r.module ?? moduleOfPath(r.path) }));
/** The screens of this build: switched-off modules have no route, menu entry or Help link. */
export const ROUTES: RouteDef[] = ROUTES_ALL.filter((r) => moduleOn(r.module));

/** Routes that sit outside the menu but inside the shell. */
export const EXTRA_ROUTES: { path: string; page: LazyExoticComponent<ComponentType> }[] = [
  { path: '/help/:slug', page: lazy(() => import('./help/HelpArticle')) },
  { path: '/account', page: lazy(() => import('./auth/Account')) },
];

const specificity = (p: string) => p.split('/').reduce((n, s) => n + (s.startsWith(':') ? 1 : 10), 0);

/** The route that best matches `pathname` (static segments beat :params). */
export function routeFor(pathname: string, routes: RouteDef[] = ROUTES): RouteDef | undefined {
  const parts = pathname.split('/').filter(Boolean);
  const matches = routes.filter((r) => {
    const rp = r.path.split('/').filter(Boolean);
    if (r.path === '/') return pathname === '/';
    if (rp.length > parts.length) return false;
    return rp.every((s, i) => s.startsWith(':') || s === parts[i]);
  });
  return matches.sort((a, b) => specificity(b.path) - specificity(a.path))[0];
}

/** NotaVera N1: routeFor over every screen, switched-off modules included (their kept tests use it). */
export const routeForAll = (pathname: string): RouteDef | undefined => routeFor(pathname, ROUTES_ALL);
